import type { AasApiAdapter } from "../adapter";
import { isHttpError, requestJson } from "../http";
import type { AgentSession } from "../types";
import type { BackendAgentSession } from "./backendTypes";
import { apiBaseUrl } from "./shared";

function mapAgentSession(session: BackendAgentSession): AgentSession {
	return {
		sessionId: session.sessionId,
		conversationId: session.conversationId,
		workspaceId: session.workspaceId,
		profileId: session.profileId,
		profileSlug: session.profileSlug,
		model: session.model,
		thinkingLevel: session.thinkingLevel,
		state: session.state,
		createdAt: session.createdAt,
		updatedAt: session.updatedAt,
	};
}

export const sessionsApi: AasApiAdapter["sessions"] = {
	async list() {
		const sessions = await requestJson<BackendAgentSession[]>("/api/agent-sessions");
		return sessions.map(mapAgentSession);
	},
	async create(input) {
		const session = await requestJson<BackendAgentSession>("/api/agent-sessions", {
			method: "POST",
			body: {
				profileSlug: input.profileSlug,
				conversationId: input.conversationId,
				runtime: input.runtime,
				metadata: input.metadata ?? {},
				files: input.files,
			},
		});
		return mapAgentSession(session);
	},
	async get(id) {
		try {
			const session = await requestJson<BackendAgentSession>(
				`/api/agent-sessions/${encodeURIComponent(id)}`,
			);
			return mapAgentSession(session);
		} catch (error) {
			if (isHttpError(error) && error.status === 404) return null;
			throw error;
		}
	},
	async submitTurn(id, input) {
		await requestJson(`/api/agent-sessions/${encodeURIComponent(id)}/turns`, {
			method: "POST",
			body: input,
		});
	},
	async command(id, command) {
		await requestJson(`/api/agent-sessions/${encodeURIComponent(id)}/commands`, {
			method: "POST",
			body: command,
		});
	},
	async snapshot(id) {
		try {
			return await requestJson(`/api/agent-sessions/${encodeURIComponent(id)}/snapshot`);
		} catch (error) {
			if (isHttpError(error) && error.status === 404) return null;
			throw error;
		}
	},
	async subscribe(id, onEvent, fromSeq = 0) {
		const url = new URL(`/api/agent-sessions/${encodeURIComponent(id)}/events`, apiBaseUrl());
		url.searchParams.set("fromSeq", String(fromSeq));
		const source = new EventSource(url.toString(), { withCredentials: true });
		source.onmessage = (event) => {
			try {
				onEvent(JSON.parse(event.data));
			} catch {
				// Ignore parse errors
			}
		};
		return () => source.close();
	},
};
